import { useCallback, useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { anmeldung, gleicheAb, offenerStand, type Abgleichzustand } from './daten/abgleich.ts';
import { db, schreibeZustand, ANMELDUNG } from './daten/db.ts';
import { fordereDauerhaftenSpeicher, type Speicherlage } from './daten/speicher.ts';
import { vermerkeStart } from './daten/offlinenachweis.ts';
import { Anmeldung } from './ui/Anmeldung.tsx';
import { Changelog } from './ui/Changelog.tsx';
import { Konflikte } from './ui/Konflikte.tsx';
import { Tickets, useOffeneTickets } from './ui/Tickets.tsx';
import { Einstellungen } from './ui/Einstellungen.tsx';
import { loeseAuf, VORGABEN, type Einstellungsquelle } from './einstellungen.ts';
import { Projektansicht, type Reiter } from './ui/Projektansicht.tsx';
import { Projektliste } from './ui/Projektliste.tsx';
import { Statusleiste } from './ui/Statusleiste.tsx';
import type { Kartenquelle } from './ui/Karte.tsx';
import type { Kalkulationsregeln } from '@pv/geometrie';

/**
 * Bis der Server das erste Mal erreicht wurde, arbeitet die Anwendung mit
 * diesen Werten. Danach kommen sie aus den Einstellungen und werden bei jedem
 * Abgleich aufgefrischt.
 */
const NOTFALLQUELLE: Kartenquelle = {
  schluessel: 'basemap-ortho',
  name: 'basemap.at Orthofoto',
  url: 'https://mapsneu.wien.gv.at/basemap/bmaporthofoto30cm/normal/google3857/{z}/{y}/{x}.jpeg',
  // basemap.at liefert das Orthofoto bis Stufe 19. Stand hier 20, war das
  // Luftbild beim Abtasten immer leer und die Dachteilung fiel aus.
  maxZoom: 19,
  aufloesungCm: 29,
  nachweis: 'Datenquelle: basemap.at',
  lizenz: 'CC-BY 4.0',
};

const NOTFALLKALKULATION: Kalkulationsregeln = {
  kabelzuschlagProzent: 15,
  stundenJeModul: 0.35,
  grundstunden: 8,
  aufschlaege: {},
};

export function App(): React.JSX.Element {
  const [angemeldet, setAngemeldet] = useState<boolean | null>(null);
  const [projektId, setProjektId] = useState<string | null>(null);
  const [startReiter, setStartReiter] = useState<Reiter | undefined>(undefined);
  const [online, setOnline] = useState(navigator.onLine);
  const [zustand, setZustand] = useState<Abgleichzustand>('offen');
  const [speicher, setSpeicher] = useState<Speicherlage | null>(null);
  const [neueVersionDa, setNeueVersionDa] = useState(false);
  const [changelogOffen, setChangelogOffen] = useState(false);
  const [konflikteOffen, setKonflikteOffen] = useState(false);
  const [ticketsOffen, setTicketsOffen] = useState(false);
  const [einstellungenOffen, setEinstellungenOffen] = useState(false);
  const [benutzer, setBenutzer] = useState<{ name: string; rolle: string } | null>(null);
  /**
   * Warum die Anwendung nicht starten kann. Ohne das stand bei jedem Fehler
   * beim Oeffnen der lokalen Datenbank "Einen Moment …" — fuenf Wochen lang,
   * ohne dass irgendwo stand, was los ist (Dexie-Version 8, Oktober 2026).
   */
  const [startfehler, setStartfehler] = useState<string | null>(null);
  /** Der Start dauert ungewoehnlich lange, ohne dass ein Fehler kam. */
  const [startHaengt, setStartHaengt] = useState(false);

  const stand = useLiveQuery(() => offenerStand(), [], { offen: 0, konflikte: 0 });
  const einstellungen = useLiveQuery(() => db.einstellungen.toArray(), [], []);
  const version = useLiveQuery(
    () => db.zustand.get('version').then((z) => (z?.wert as string) ?? 'dev'),
    [],
    'dev',
  );
  const offeneTickets = useOffeneTickets(online && angemeldet === true);
  const offenesProjekt = useLiveQuery(
    () => (projektId ? db.projekte.get(projektId) : undefined),
    [projektId],
  );

  // Alles, was eingestellt werden kann, laeuft ueber denselben Resolver:
  // Projekteinstellung vor globaler Einstellung vor Notfallwert. Das Projekt
  // liefert seine Ueberschreibungen selbst nach — hier gilt der globale Stand.
  const quelle: Einstellungsquelle = { global: einstellungen, jeProjekt: [] };
  const quellen = leseQuellen(einstellungen);
  const standardregeln = loeseAuf(quelle, 'raster.standard', VORGABEN['raster.standard']).wert;
  const kalkulation = leseKalkulation(einstellungen);

  // Rolle und Name kommen immer aus der abgelegten Anmeldung, nie aus einem
  // Zustand, den die Anmeldemaske nebenher setzt. Genau das fehlte: nach einer
  // frischen Anmeldung stand "angemeldet", aber die Rolle war noch leer — und
  // damit galt ueberall die schwaechste, "vertrieb". Der Verwaltungsbereich war
  // erst nach einem Neuladen zu sehen.
  const uebernehmeAnmeldung = useCallback(async () => {
    const a = await anmeldung();
    setAngemeldet(a !== null);
    setBenutzer(a ? { name: a.benutzer.name, rolle: a.benutzer.rolle } : null);
  }, []);

  // Haengt der Start, ohne zu scheitern, kommt kein Fehler, den man fangen
  // koennte — etwa wenn ein zweiter Tab mit der alten Fassung die Datenbank
  // offen haelt und Dexie auf ihn wartet. Dann wenigstens nach ein paar
  // Sekunden sagen, dass etwas nicht stimmt.
  useEffect(() => {
    if (angemeldet !== null) return;
    const zeit = window.setTimeout(() => setStartHaengt(true), 8000);
    return () => window.clearTimeout(zeit);
  }, [angemeldet]);

  useEffect(() => {
    uebernehmeAnmeldung().catch((fehler: unknown) => {
      const art = fehler instanceof Error ? fehler.name : '';
      const text = fehler instanceof Error ? fehler.message : String(fehler);
      setStartfehler(art && !text.startsWith(art) ? `${art}: ${text}` : text);
    });
    // Dauerhaften Speicher gleich beim Start anfordern (PV-B27). Kommt hier ein
    // Nein zurueck, ist das Geraet fuer den Aussendienst nicht geeignet — genau
    // die Frage, die PV-B30 auf dem Zielgeraet beantwortet.
    void fordereDauerhaftenSpeicher().then(setSpeicher);
    // Ob die Anwendung ohne Verbindung angelaufen ist, muss in diesem Moment
    // festgehalten werden — hinterher ist es eine Behauptung (PV-B30).
    void vermerkeStart(navigator.onLine);
  }, [uebernehmeAnmeldung]);

  useEffect(() => {
    const rauf = (): void => setOnline(true);
    const runter = (): void => setOnline(false);
    const version = (): void => setNeueVersionDa(true);
    window.addEventListener('online', rauf);
    window.addEventListener('offline', runter);
    window.addEventListener('pv:neue-version', version);
    return () => {
      window.removeEventListener('online', rauf);
      window.removeEventListener('offline', runter);
      window.removeEventListener('pv:neue-version', version);
    };
  }, []);

  /**
   * Nach dem Abmelden landet der Nächste nicht im offenen Projekt oder in den
   * Einstellungen seines Vorgängers — mit der eigenen Rolle.
   */
  const verlasseSitzung = useCallback(() => {
    setAngemeldet(false);
    setBenutzer(null);
    setProjektId(null);
    setStartReiter(undefined);
    setEinstellungenOffen(false);
    setTicketsOffen(false);
    setKonflikteOffen(false);
  }, []);

  const abgleichen = useCallback(async () => {
    setZustand('uebertraegt');
    const bericht = await gleicheAb();
    setZustand(bericht.zustand);
    // Gilt die Sitzung nicht mehr, hilft kein weiterer Versuch — dann gehoert
    // der Anmeldebildschirm hin, nicht eine Meldung, die stehen bleibt.
    if (bericht.abgemeldet) {
      verlasseSitzung();
      return;
    }
    void fetch('/api/version')
      .then((a) => a.json())
      .then((d: { version: string }) => schreibeZustand('version', d.version))
      .catch(() => undefined);
  }, [verlasseSitzung]);

  // Sobald wieder Verbindung besteht, wird uebertragen — ohne dass jemand einen
  // Knopf druecken muss. Der Knopf bleibt trotzdem, damit man es erzwingen kann.
  useEffect(() => {
    if (angemeldet && online) void abgleichen();
  }, [angemeldet, online, abgleichen]);

  // Kein Knopf "lokale Daten loeschen": Was in der Warteschlange liegt, war
  // nie am Server. Was hier angeboten wird, ist eine neue Fassung — die liegt
  // als wartender Service Worker bereit, und der Knopf dafuer sitzt sonst in
  // der Kopfzeile. Die wird in diesem Zustand nicht gezeichnet; ohne diesen
  // Knopf kaeme ein haengendes Geraet nur durch komplettes Schliessen der
  // Anwendung an die Reparatur. Die Regel "nicht aktualisieren, solange
  // gearbeitet wird" (PV-B22) greift hier nicht: Es kann ja niemand arbeiten,
  // und ein Update laesst den lokalen Bestand unberuehrt.
  const neuStarten = (): void => {
    if (neueVersionDa) window.dispatchEvent(new CustomEvent('pv:version-uebernehmen'));
    else window.location.reload();
  };
  const startknopf = (
    <button className="wichtig" onClick={neuStarten}>
      {neueVersionDa ? 'Neue Version übernehmen' : 'Neu laden'}
    </button>
  );

  if (startfehler) {
    return (
      <div className="liste">
        <div className="hinweis hinweis--fehler">
          <strong>Die Anwendung kann ihren lokalen Datenbestand nicht öffnen.</strong>
          <br />
          Die Daten auf diesem Gerät sind dabei nicht verloren — sie werden nur gerade nicht
          gelesen. Bitte diese Meldung an den Betreuer weitergeben und nichts am Browser
          zurücksetzen.
          <br />
          <code style={{ display: 'block', marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>
            {startfehler}
          </code>
        </div>
        {startknopf}
      </div>
    );
  }
  if (angemeldet === null) {
    return (
      <div className="liste">
        <p>Einen Moment …</p>
        {startHaengt && (
          <>
            <div className="hinweis hinweis--warnung">
              Der Start dauert ungewöhnlich lange. Ist die Anwendung noch in einem zweiten Tab
              oder Fenster offen, diesen bitte schließen — er hält den lokalen Datenbestand
              fest.
            </div>
            {startknopf}
          </>
        )}
      </div>
    );
  }
  if (!angemeldet) return <Anmeldung onFertig={() => void uebernehmeAnmeldung()} />;

  return (
    <div className="rahmen">
      <Statusleiste
        online={online}
        zustand={zustand}
        offen={stand.offen}
        konflikte={stand.konflikte}
        speicher={speicher}
        version={version}
        neueVersionDa={neueVersionDa}
        // Nie aktualisieren, waehrend Aenderungen offen sind (PV-B22).
        darfAktualisieren={stand.offen === 0}
        onAbgleich={() => void abgleichen()}
        onAktualisieren={() => window.dispatchEvent(new CustomEvent('pv:version-uebernehmen'))}
        onChangelog={() => setChangelogOffen(true)}
        onKonflikte={() => setKonflikteOffen(true)}
        offeneTickets={offeneTickets}
        onTickets={() => setTicketsOffen(true)}
        onEinstellungen={() => setEinstellungenOffen(true)}
        onAbmelden={() => {
          // Was noch nicht am Server ist, ginge sonst unter der Anmeldung des
          // Nächsten hinaus — und würde womöglich abgewiesen.
          if (stand.offen > 0) {
            alert(`${stand.offen} Änderung(en) sind noch nicht am Server. Bitte erst abgleichen, dann abmelden.`);
            return;
          }
          const frage = navigator.onLine
            ? 'Abmelden?'
            : 'Ohne Verbindung kann sich danach niemand anmelden, bis wieder Empfang da ist. Trotzdem abmelden?';
          if (!confirm(frage)) return;
          void schreibeZustand(ANMELDUNG, null).then(verlasseSitzung);
        }}
      />

      {konflikteOffen && <Konflikte onSchliessen={() => setKonflikteOffen(false)} />}

      {ticketsOffen && (
        <Tickets
          online={online}
          projekt={
            offenesProjekt
              ? { id: offenesProjekt.id, name: offenesProjekt.kunde_name || 'Projekt' }
              : null
          }
          version={version}
          istVerwalter={benutzer?.rolle === 'admin'}
          onSchliessen={() => setTicketsOffen(false)}
        />
      )}

      {changelogOffen && (
        <Changelog version={version} onSchliessen={() => setChangelogOffen(false)} />
      )}

      {einstellungenOffen && (
        <Einstellungen
          projektId={projektId}
          rolle={benutzer?.rolle ?? 'vertrieb'}
          onSchliessen={() => setEinstellungenOffen(false)}
        />
      )}

      <div className="inhalt">
        {projektId ? (
          <Projektansicht
            projektId={projektId}
            quellen={quellen}
            vorladen={leseVorladen(einstellungen)}
            online={online}
            offeneAenderungen={stand.offen}
            rolle={benutzer?.rolle ?? 'vertrieb'}
            {...(startReiter ? { startReiter } : {})}
            onZurueck={() => setProjektId(null)}
          />
        ) : (
          <Projektliste
            quelle={quellen[0]!}
            vorladen={leseVorladen(einstellungen)}
            onOeffnen={(id, reiter) => {
              setStartReiter(reiter);
              setProjektId(id);
            }}
          />
        )}
      </div>
    </div>
  );
}

interface Einstellungszeile {
  schluessel: string;
  wert: unknown;
}

function wertVon<T>(zeilen: Einstellungszeile[], schluessel: string, vorgabe: T): T {
  const zeile = zeilen.find((z) => z.schluessel === schluessel);
  return zeile ? (zeile.wert as T) : vorgabe;
}

function leseQuellen(zeilen: Einstellungszeile[]): Kartenquelle[] {
  const karte = wertVon<{ quellen?: Kartenquelle[] }>(zeilen, 'karte.quellen', {});
  return karte.quellen && karte.quellen.length > 0 ? karte.quellen : [NOTFALLQUELLE];
}

function leseKalkulation(zeilen: Einstellungszeile[]): Kalkulationsregeln {
  const gespeichert = wertVon<Record<string, unknown>>(zeilen, 'kalkulation.standard', {});
  const zahl = (wert: unknown, vorgabe: number): number =>
    typeof wert === 'number' && Number.isFinite(wert) ? wert : vorgabe;

  const regeln: Kalkulationsregeln = {
    kabelzuschlagProzent: zahl(
      gespeichert['kabelzuschlagProzent'],
      NOTFALLKALKULATION.kabelzuschlagProzent,
    ),
    stundenJeModul: zahl(gespeichert['stundenJeModul'], NOTFALLKALKULATION.stundenJeModul),
    grundstunden: zahl(gespeichert['grundstunden'], NOTFALLKALKULATION.grundstunden),
    aufschlaege: (gespeichert['aufschlaege'] as Kalkulationsregeln['aufschlaege']) ?? {},
  };
  // Ein Stundensatz von null heisst "noch nicht eingetragen". Wer ihn hier
  // durchreicht, schreibt eine Montage um null Euro ins Kundenangebot.
  if (typeof gespeichert['stundensatz'] === 'number') {
    regeln.stundensatz = gespeichert['stundensatz'];
  }
  return regeln;
}

function lesePreise(zeilen: Einstellungszeile[]): Partial<import('./typen.ts').Preisannahmen> {
  // Strompreis und Verguetung sind absichtlich ohne Vorgabewert. Fehlen sie,
  // gibt es keine Amortisation, sondern einen Hinweis — geraten wird nichts.
  return wertVon(zeilen, 'wirtschaftlichkeit.preise', { preissteigerungProzent: 3 });
}

function leseAnlagenwerte(zeilen: Einstellungszeile[]): {
  anlagengueteProzent: number;
  speicherpreisJeKwh: number | null;
  speichergroessen: number[];
} {
  const wert = wertVon<{
    anlagengueteProzent?: number;
    speicherpreisJeKwh?: number | null;
    speichergroessen?: number[];
  }>(zeilen, 'wirtschaftlichkeit.anlage', {});
  return {
    anlagengueteProzent: wert.anlagengueteProzent ?? 84,
    speicherpreisJeKwh: wert.speicherpreisJeKwh ?? null,
    speichergroessen: wert.speichergroessen ?? [5, 10, 15, 20],
  };
}

function leseVorladen(zeilen: Einstellungszeile[]): {
  umkreisM: number;
  minZoom: number;
  maxZoom: number;
} {
  return wertVon(zeilen, 'offline.vorladen', { umkreisM: 200, minZoom: 15, maxZoom: 19 });
}
